import type { ScheduleRow, SeatOut } from '@conciair/scheduler-core';

/** `${tail}|${rotationStart}|${seat}` */
export type SeatKey = string;
export const seatKey = (tail: string, rotationStart: string, seat: 'PIC' | 'SIC'): SeatKey => `${tail}|${rotationStart}|${seat}`;
export const windowKey = (tail: string, rotationStart: string) => `${tail}|${rotationStart}`;

export interface SeatChange {
  tail: string;
  rotationStart: string;
  seat: 'PIC' | 'SIC';
  before: string | null;
  after: string | null;
  kind: 'changed' | 'added' | 'removed';
}

const idOf = (s: SeatOut | undefined) => (s && s.pilotId ? s.pilotId : null);

/**
 * Seat-by-seat differences between two schedules (spec 5.3 Compare, 6.2
 * publish diff). Keys are seat keys; a window present in only one side is
 * reported as added or removed on both seats.
 */
export function diffSchedules(base: ScheduleRow[], next: ScheduleRow[]): Map<SeatKey, SeatChange> {
  const out = new Map<SeatKey, SeatChange>();
  const baseBy = new Map(base.map((r) => [windowKey(r.tail, r.rotationStart), r]));
  const nextBy = new Map(next.map((r) => [windowKey(r.tail, r.rotationStart), r]));
  for (const [k, b] of baseBy) {
    const n = nextBy.get(k);
    for (const seat of ['PIC', 'SIC'] as const) {
      const before = idOf(seat === 'PIC' ? b.pic : b.sic);
      const after = n ? idOf(seat === 'PIC' ? n.pic : n.sic) : null;
      if (!n) out.set(seatKey(b.tail, b.rotationStart, seat), { tail: b.tail, rotationStart: b.rotationStart, seat, before, after: null, kind: 'removed' });
      else if (before !== after) out.set(seatKey(b.tail, b.rotationStart, seat), { tail: b.tail, rotationStart: b.rotationStart, seat, before, after, kind: 'changed' });
    }
  }
  for (const [k, n] of nextBy) {
    if (baseBy.has(k)) continue;
    for (const seat of ['PIC', 'SIC'] as const) {
      out.set(seatKey(n.tail, n.rotationStart, seat), { tail: n.tail, rotationStart: n.rotationStart, seat, before: null, after: idOf(seat === 'PIC' ? n.pic : n.sic), kind: 'added' });
    }
  }
  return out;
}

/** Windows that have at least one changed seat. */
export function changedWindows(diff: Map<SeatKey, SeatChange>): Set<string> {
  const s = new Set<string>();
  for (const c of diff.values()) s.add(windowKey(c.tail, c.rotationStart));
  return s;
}
